<template>
  <div class="comp-context">
    <ul ref="container">
      <li
        v-for="item in computedActions"
        :key="item.id"
        :class="{ disabled: item.disabled }"
        @click="handleClick(item)">
        <Icon v-if="item.icon" :name="item.icon" />
        <span v-text="item.label" />
      </li>
    </ul>
  </div>
</template>

<script>
  //----------------------------------
  // Private Properties
  //----------------------------------
  import DomUtil from '@/utils/DomUtil';
  import Icon from '@/components/ui/Icon';

  const ALIGN_TOP = 'top';
  const ALIGN_RIGHT = 'right';
  const ALIGN_BOTTOM = 'bottom';
  const ALIGN_LEFT = 'left';
  const ALIGN_CENTER = 'center';
  const ALIGN_MIDDLE = 'middle';

  export default {
    name: 'ContextMenu',
    components: {
      Icon,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      origin: {
        type: [HTMLElement, SVGElement, Object],
        default: null,
      },
      offsetX: {
        type: Number,
        default: -16,
      },
      offsetY: {
        type: Number,
        default: 16,
      },
      align: {
        type: String,
        default: 'bottom left',
      },
      actions: {
        type: Array,
        default: null,
      },
      reference: {
        type: Object,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      computedActions() {
        const actions = this.actions || [];
        const out = [];
        let id = 0;
        actions.forEach((item) => {
          if (item) {
            if (item.handler) {
              const context = item.handler(this.reference);
              if (context) {
                context.id = id;
                out.push(context);
              }
            } else {
              // eslint-disable-next-line no-param-reassign
              item.id = id;
              out.push(item);
            }
            id += 1;
          }
        });
        return out;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
    },
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    created() {
      this.$nextTick(() => {
        window.addEventListener('click', this.handleWindowEvents);
        window.addEventListener('scroll', this.handleWindowEvents);
        window.addEventListener('resize', this.handleWindowEvents);
        this.position();
      });
    },
    beforeDestroy() {
      window.removeEventListener('click', this.handleWindowEvents);
      window.removeEventListener('scroll', this.handleWindowEvents);
      window.removeEventListener('resize', this.handleWindowEvents);
      if (this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el);
      }
    },
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      position() {
        const { container } = this.$refs;
        let originPoint;
        if (this.origin instanceof Element) {
          const rect = this.origin.getBoundingClientRect();
          originPoint = { x: rect.left + (rect.width / 2), y: rect.top + (rect.height / 1.5) };
        } else {
          originPoint = this.origin;
        }
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        const [valign, halign] = this.align.trim().split(' ');
        let x = originPoint.x;
        let y = originPoint.y;

        // Horizontal Alignments
        if (halign === ALIGN_LEFT) {
          x -= (containerWidth + this.offsetX);
        }
        if (halign === ALIGN_RIGHT) {
          x += this.offsetX;
        }
        if (halign === ALIGN_CENTER || halign === ALIGN_MIDDLE) {
          x -= containerWidth / 2;
        }

        // Ensure "x" stays in viewport ( sort of )
        const scrollbarWidth = (window.innerWidth !== document.body.scrollWidth) ? DomUtil.defaultScrollbarWidth : 0;
        if ((x + containerWidth) > (window.innerWidth - scrollbarWidth)) {
          x -= ((x + containerWidth) - (window.innerWidth - scrollbarWidth - this.offsetX));
        } else if (x <= 0) {
          x = this.offsetX;
        }

        // Vertical Alignments
        if (valign === ALIGN_TOP) {
          y -= (containerHeight + this.offsetY);
        }
        if (valign === ALIGN_BOTTOM) {
          y += this.offsetY;
        }
        if (valign === ALIGN_CENTER || valign === ALIGN_MIDDLE) {
          y -= containerHeight / 2;
        }

        // Ensure "y" stays in viewport ( sort of )
        if (valign === ALIGN_BOTTOM && (y + containerHeight) > window.innerHeight) {
          y = (originPoint.y - this.offsetY) - containerHeight;
        }
        if (y <= 0) {
          y = this.offsetY;
        }
        if (valign !== ALIGN_TOP) {
          container.style.maxHeight = `${window.innerHeight - y - this.offsetY}px`;
        } else {
          container.style.maxHeight = `${originPoint.y - y - this.offsetY}px`;
        }

        container.style.left = `${Math.floor(x)}px`;
        container.style.top = `${Math.floor(y)}px`;
      },
      destroy() {
        this.$destroy();
      },
      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleWindowEvents(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        if (['resize', 'scroll'].includes(evt.type) || evt.target.classList.contains('comp-context')) {
          this.destroy();
        }
      },
      async handleClick(item) {
        if (typeof item.action === 'function') {
          await item.action(this.reference, this);
          if (!item.keepAlive) {
            this.destroy();
          }
        } else {
          this.$emit('action', item.action, this.reference);
        }
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.comp-context {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 200;

  & > ul {
    position: absolute;
    display: inline-block;
    list-style-type: none;
    padding: 0;
    margin: 0;
    min-width: 184px;
    background-color: $color-white-solid-10;
    box-shadow: $box-shadow-5;

    li {
      position: relative;
      height: 38px;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      padding: 0 18px;
      cursor: pointer;
      z-index: 1;

      &:before {
        position: absolute;
        content: '';
        left: 4px;
        top: 2px;
        width: calc(100% - 8px);
        height: calc(100% - 4px);
        background-color: transparent;
        will-change: background-color;
        transition: background-color 0.2s linear;
        border-radius: $default-border-radius;
        z-index: -1;
      }

      &:hover:before {
        background-color: $color-black-alpha-05;
      }

      &.disabled {
        opacity: 0.5;
        pointer-events: none;
      }

      & > svg {
        width: 14px;
        height: 14px;

        & + span {
          margin-left: 8px;
          user-select: none;
        }
      }

      & > span {
        @include font-body-small;
        color: $color-black-solid-100;
      }
    }
  }
}
</style>
